import { useEffect } from "react";
import { useTheme } from "@/components/admin/use-theme";
import { useConfigurationContext } from "./ConfigurationContext";

const DEFAULT_FAVICON = "/favicon.png";
const DEFAULT_TITLE = "Dotus CRM";

const extractSrc = (logo: any): string => {
  if (!logo) return "";
  if (typeof logo === "string") return logo.trim();
  if (typeof logo === "object") {
    if (logo.src && typeof logo.src === "string") return logo.src.trim();
    if (logo.url && typeof logo.url === "string") return logo.url.trim();
  }
  return "";
};

const applyFaviconTags = (href: string) => {
  if (!href) return;

  // Remove tags anteriores para forçar o navegador (Chrome/Brave/Edge) a desconsiderar o cache
  const existing = document.querySelectorAll<HTMLLinkElement>(
    "link[rel*='icon'], link[rel='apple-touch-icon']",
  );
  existing.forEach((el) => el.remove());

  const link = document.createElement("link");
  link.rel = "icon";
  link.type =
    href.startsWith("data:image/svg") || href.endsWith(".svg")
      ? "image/svg+xml"
      : "image/png";
  link.href = href;
  document.head.appendChild(link);

  const shortcut = document.createElement("link");
  shortcut.rel = "shortcut icon";
  shortcut.href = href;
  document.head.appendChild(shortcut);

  const apple = document.createElement("link");
  apple.rel = "apple-touch-icon";
  apple.href = href;
  document.head.appendChild(apple);
};

const updateFavicon = (rawUrl: string) => {
  const url = rawUrl?.trim() ? rawUrl : DEFAULT_FAVICON;

  // 1. Aplicação imediata da URL/DataURI
  applyFaviconTags(url);

  // 2. Se for imagem customizada, redimensiona via Canvas (64x64) para garantir nitidez e renderização imediata na aba
  if (url && url !== DEFAULT_FAVICON && !url.endsWith(".ico")) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, 64, 64);
          ctx.drawImage(img, 0, 0, 64, 64);
          const pngUrl = canvas.toDataURL("image/png");
          applyFaviconTags(pngUrl);
        }
      };
      img.src = url;
    } catch {
      // Se houver restrição de CORS em URL externa, a aplicação direta (passo 1) permanece ativa
    }
  }
};

/**
 * Hook responsável por atualizar dinamicamente o Favicon e o Título da aba do navegador
 * de acordo com a logo (Light/Dark Mode) e o título configurados no CRM ativo.
 */
export const useDynamicBranding = () => {
  const { title, darkModeLogo, lightModeLogo } = useConfigurationContext();
  const { theme } = useTheme();

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    // 1. Atualiza o título da aba
    document.title = title?.trim() ? title : DEFAULT_TITLE;

    // 2. Determina o tema ativo
    let isDark = false;
    if (theme === "dark") {
      isDark = true;
    } else if (theme === "system") {
      isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    }

    // 3. Extrai as fontes das logos (suportando tanto string direta quanto objetos { src: '...' })
    const darkSrc = extractSrc(darkModeLogo);
    const lightSrc = extractSrc(lightModeLogo);

    const activeLogo = isDark ? darkSrc || lightSrc : lightSrc || darkSrc;

    // 4. Aplica o Favicon na aba
    updateFavicon(activeLogo);
  }, [title, darkModeLogo, lightModeLogo, theme]);

  // Listener para mudança de tema quando configurado como "system"
  useEffect(() => {
    if (typeof window === "undefined" || theme !== "system") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const isDark = mediaQuery.matches;
      const darkSrc = extractSrc(darkModeLogo);
      const lightSrc = extractSrc(lightModeLogo);
      const activeLogo = isDark ? darkSrc || lightSrc : lightSrc || darkSrc;
      updateFavicon(activeLogo);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, darkModeLogo, lightModeLogo]);
};
